import { useCallback, useMemo, useRef } from 'react';

export const usePreloadVideos = (videoUrls: (string | undefined)[]) => {
    const loadedVideosRef = useRef<Set<string>>(new Set());
    const videoElementsRef = useRef<Map<string, HTMLVideoElement>>(new Map());

    // Memoize valid URLs to create a stable reference
    const validUrls = useMemo(() => videoUrls.filter((url): url is string => Boolean(url)), [videoUrls]);

    const preloadVideos = useCallback((): Promise<void> => {
        return new Promise(resolve => {
            if (validUrls.length === 0) {
                resolve();
                return;
            }

            // Check if all videos are already loaded
            const allLoaded = validUrls.every(url => loadedVideosRef.current.has(url));
            if (allLoaded) {
                resolve();
                return;
            }

            let loadedCount = 0;
            const totalVideos = validUrls.length;
            const videoElements = new Map<string, HTMLVideoElement>();
            const currentRef = videoElementsRef.current;

            const handleVideoLoaded = (url: string) => {
                loadedVideosRef.current.add(url);
                loadedCount++;

                if (loadedCount === totalVideos) {
                    resolve();
                }
            };

            const handleVideoError = (url: string) => {
                // Even if video fails to load, count it as "loaded" to not block the modal
                loadedVideosRef.current.add(url);
                loadedCount++;

                if (loadedCount === totalVideos) {
                    resolve();
                }
            };

            // Preload all videos
            validUrls.forEach(url => {
                if (loadedVideosRef.current.has(url)) {
                    loadedCount++;
                    if (loadedCount === totalVideos) {
                        resolve();
                    }
                    return;
                }

                const video = document.createElement('video');
                video.preload = 'auto';
                video.muted = true;
                video.playsInline = true;

                video.addEventListener('canplaythrough', () => handleVideoLoaded(url), { once: true });
                video.addEventListener('error', () => handleVideoError(url), { once: true });

                video.src = url;
                video.load();

                videoElements.set(url, video);
                currentRef.set(url, video);
            });
        });
    }, [validUrls]);

    return preloadVideos;
};
